// @ts-nocheck
import React, { useMemo, useState } from 'react';
import { CheckCircle, AlertTriangle, ArrowRight, ArrowLeft, ChevronDown, ChevronUp, Database, FileText } from 'lucide-react';
import logger from '../../utils/logger';
import './ComparativeSummary.css';

const ComparativeSummary = ({ discoveryData, onProcess, onBack, loading, hasExistingResults = false, onViewResults }) => {
  const { rawData, rawSheetName, comparables, alignment } = discoveryData;
  const [warningsOpen, setWarningsOpen] = useState(false);
  
  const rawEntities = rawData.entities || [];
  const accountsCount = rawData.accounts?.length || 0;
  const rawDateSummary = useMemo(() => formatDateRange(rawData.date_range), [rawData.date_range]);
  
  // Get period summaries for each comparable
  const comparableSummaries = useMemo(() => 
    comparables.map((comp) => ({
      name: comp.sheet,
      periodCount: comp.data.period_options?.length || 0,
      range: summarizeComparablePeriods(comp.data.period_options),
    })),
    [comparables]
  );
  
  const commonPeriods = alignment.common_periods || [];
  const warnings = useMemo(() => alignment.warnings || [], [alignment.warnings]);
  const warningBuckets = useMemo(() => organizeWarnings(warnings), [warnings]);
  const hasError = alignment.status === 'error';

  const handleProcess = () => {
    onProcess(commonPeriods); 
  };

  return (
    <div className="comparative-summary-container">
      {/* Compact Header */}
      <div className="summary-header">
        <h2>Confirm Period Alignment</h2>
        <p>Review the discovered data and common periods before processing.</p>
      </div>

      {/* Data Sources Section */}
      <div className="sources-section">
        {/* Raw Data Row */}
        <div className="source-row">
          <Database size={14} className="source-icon raw" />
          <span className="source-type">Raw</span>
          <span className="source-name">{rawSheetName}</span>
          <span className="source-meta">{rawEntities.length} entities • {accountsCount} accounts</span>
          {rawDateSummary && <span className="source-range">{rawDateSummary}</span>}
        </div>
        
        {/* Comparables Group */}
        <div className="source-group">
          <div className="source-group-header">
            <FileText size={14} className="source-icon comp" />
            <span className="source-type">Comparables</span>
            <span className="source-count">{comparables.length} sheets</span>
          </div>
          <div className="source-group-items">
            {comparableSummaries.map((comp, idx) => (
              <div key={idx} className="source-item">
                <span className="source-name">{comp.name}</span>
                <span className="source-meta">{comp.periodCount} periods</span>
                {comp.range && <span className="source-range">{comp.range}</span>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Common Periods - Main Focus */}
      <div className={`periods-section ${hasError ? 'error' : ''}`}>
        <div className="periods-header">
          <div className="periods-title">
            <span className="periods-count">{commonPeriods.length}</span>
            <span>Common Periods</span>
          </div>
          {!hasError && commonPeriods.length > 0 && (
            <span className="status-pill success">
              <CheckCircle size={12} />
              Aligned
            </span>
          )}
          {hasError && (
            <span className="status-pill error">
              <AlertTriangle size={12} />
              Failed
            </span>
          )}
        </div>

        {!hasError && commonPeriods.length > 0 ? (
          <div className="periods-list">
            {commonPeriods.map((period, idx) => (
              <span key={idx} className="period-tag">
                {period.common_name}
              </span>
            ))}
          </div>
        ) : (
          <div className="no-periods">
            <AlertTriangle size={16} />
            <span>No common periods found across all sheets</span>
          </div>
        )}
      </div>

      {/* Warnings Dropdown */}
      {warnings.length > 0 && (
        <div className="warnings-dropdown">
          <button 
            className="warnings-toggle"
            onClick={() => setWarningsOpen(!warningsOpen)}
          >
            <AlertTriangle size={14} />
            <span>{warnings.length} alignment {warnings.length === 1 ? 'warning' : 'warnings'}</span>
            {warningsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          
          {warningsOpen && (
            <div className="warnings-content">
              {warningBuckets.missing.length > 0 && (
                <div className="warning-group">
                  <span className="warning-group-title">Periods skipped (missing data):</span>
                  <ul>
                    {warningBuckets.missing.map(({ period, sources }) => (
                      <li key={period}>
                        <strong>{period}</strong> — missing in {sources.join(', ')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {warningBuckets.invalid.length > 0 && (
                <div className="warning-group">
                  <span className="warning-group-title">Non-calendar columns ignored:</span>
                  <ul>
                    {warningBuckets.invalid.map((period) => (
                      <li key={period}>{period}</li>
                    ))}
                  </ul>
                </div>
              )}
              {warningBuckets.other.length > 0 && (
                <div className="warning-group">
                  <span className="warning-group-title">Other:</span>
                  <ul>
                    {warningBuckets.other.map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="action-row">
        <button onClick={onBack} className="btn-back" disabled={loading}>
          <ArrowLeft size={14} />
          Back
        </button>
        <div className="action-row-right">
          {hasExistingResults && onViewResults && (
            <button 
              onClick={onViewResults} 
              className="btn-view-results"
              disabled={loading}
            >
              View Results
              <ArrowRight size={14} />
            </button>
          )}
          <button 
            onClick={handleProcess} 
            className="btn-process"
            disabled={loading || hasError || commonPeriods.length === 0}
          >
            {loading ? 'Processing...' : 'Process Data'}
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

const formatDateRange = (dateRange) => {
  if (!dateRange?.start || !dateRange?.end) return '';
  try {
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    if (Number.isNaN(start) || Number.isNaN(end)) return '';
    const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' });
    return `${formatter.format(start)} – ${formatter.format(end)}`;
  } catch (error) {
    logger.warn('Failed to format date range', error);
    return '';
  }
};

const summarizeComparablePeriods = (periodOptions = []) => {
  if (!Array.isArray(periodOptions) || periodOptions.length === 0) {
    return '';
  }
  const chronological = periodOptions
    .filter((p) => p.display_name)
    .map((p) => p.display_name);
  if (chronological.length === 0) return '';
  const first = chronological[0];
  const last = chronological[chronological.length - 1];
  return first === last ? first : `${first} – ${last}`;
};

const organizeWarnings = (warnings) => {
  const missingMap = new Map();
  const invalid = [];
  const other = [];

  warnings.forEach((warning) => {
    const missingMatch = warning.match(/^(.*) dropped because it is missing in (.+)\.?$/i);
    if (missingMatch) {
      const period = missingMatch[1].trim();
      const source = missingMatch[2].replace('.', '').trim();
      if (!missingMap.has(period)) missingMap.set(period, new Set());
      missingMap.get(period).add(source);
      return;
    }
    if (/not a calendar month/i.test(warning)) {
      const period = warning.replace('dropped because it is not a calendar month.', '').trim();
      invalid.push(period);
      return;
    }
    other.push(warning);
  });

  return {
    missing: Array.from(missingMap.entries()).map(([period, sources]) => ({
      period,
      sources: Array.from(sources),
    })),
    invalid,
    other,
  };
};

export default ComparativeSummary;
